import React from 'react';
import './scss/Home.scss';
import { Link } from 'react-router-dom';
import Logo, { LogoLight } from '../components/Sidebar/Logo';
import { useTheme } from '../context/ThemeContext';

const About = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || theme === 'system';

  return (
    <div className="landing-page">
      {/* Simple landing header (same style as Home) */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            {isDark ? <Logo width="120px" /> : <LogoLight width="120px" />}
          </div>
          <div className="nav-links">
            <Link to="/">Home</Link>
            <a href="/about">About</a>
            <a href="/contact">Contact</a>
          </div>
          <div className="nav-actions">
            <Link to="/login" className="nav-login">Log In</Link>
            <Link to="/signup" className="nav-signup">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" style={{ paddingTop: '6rem', paddingBottom: '4rem' }}>
        <div className="hero__container">
          <div className="hero__content">
            <div className="hero__text" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              <h1 className="hero__title">
                About <span className="hero__title-accent">Zoobbe</span>
              </h1>
              <p className="hero__subtitle">
                Modern collaboration inspired by Trello, crafted for speed, clarity, and trust. We're building the future of teamwork, one board at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>Our Story</h2>
            <p>Born from the need for better team collaboration tools</p>
          </div>

          <div className="steps-grid">
            <div className="step">
              <div className="step-number">🎯</div>
              <div className="step-content">
                <h3>Our Mission</h3>
                <p>To help teams move faster with a delightful, reliable tool that stays out of the way and lets work flow naturally. We believe great software should feel invisible while making the impossible possible.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">⚡</div>
              <div className="step-content">
                <h3>Our Principles</h3>
                <p>Performance first, smooth UX, accessible by default, and opinionated where it matters. We prioritize speed, simplicity, and user experience in every decision we make.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">🤝</div>
              <div className="step-content">
                <h3>Our Promise</h3>
                <p>Transparent pricing, clear communication, and continuous improvement with your feedback. We're committed to building a product that grows with your team's needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="features">
        <div className="container">
          <div className="section-header">
            <h2>What We Stand For</h2>
            <p>The values that guide everything we do</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>🚀</span>
              </div>
              <h3>Speed & Performance</h3>
              <p>Every interaction should feel instant. We obsess over performance so you can focus on what matters most.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>🎨</span>
              </div>
              <h3>Beautiful Design</h3>
              <p>Great tools should be a joy to use. We craft every pixel with care to create an experience you'll love.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>🔒</span>
              </div>
              <h3>Privacy & Security</h3>
              <p>Your data is yours. We implement enterprise-grade security while maintaining transparency about our practices.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>🌍</span>
              </div>
              <h3>Accessibility</h3>
              <p>Everyone should be able to collaborate effectively. We build with accessibility in mind from day one.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>💡</span>
              </div>
              <h3>Innovation</h3>
              <p>We're not just copying what exists. We're reimagining how teams can work together in the modern world.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <span style={{ fontSize: '2rem' }}>❤️</span>
              </div>
              <h3>Community First</h3>
              <p>Our users are our partners. We listen, learn, and build based on real feedback from real teams.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Built by a Passionate Team</h2>
            <p>Meet the people behind Zoobbe</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"We started Zoobbe because we were frustrated with existing tools. They were either too complex or too simple, never just right. We wanted to build something that felt natural."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>ZT</span>
                </div>
                <div className="author-info">
                  <h4>Zoobbe Team</h4>
                  <p>Founders & Developers</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"Every feature we build is tested with real teams. We don't ship anything until we're confident it makes collaboration better, not more complicated."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>UX</span>
                </div>
                <div className="author-info">
                  <h4>Design Team</h4>
                  <p>User Experience</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"Our goal is simple: make Zoobbe so good that teams can't imagine working any other way. We're just getting started."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>ENG</span>
                </div>
                <div className="author-info">
                  <h4>Engineering Team</h4>
                  <p>Product Development</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join Our Journey?</h2>
            <p>Help us build the future of team collaboration. Start using Zoobbe today and be part of our growing community.</p>
            <div className="cta-actions">
              <Link to="/signup" className="cta-button primary">Get Started Free</Link>
              <Link to="/contact" className="cta-button secondary">Get in Touch</Link>
            </div>
            <p className="cta-note">No credit card required • Free forever plan available</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

